import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { describeAxiosError, formatINR } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { useTaxYear } from "@/context/TaxYearContext";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import { TaxYearSelect } from "@/components/TaxYearSelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, FileCheck2, ReceiptIndianRupee, Sparkles, Plus, Trash2, ArrowRightCircle, Landmark, AlertTriangle, Download, Bell } from "lucide-react";
import { toast } from "sonner";

const FILING_STATUSES = ["Not Filed", "Prepared", "Filed", "E-Verified", "Processed", "Refund Issued", "Demand Raised"];
const CHALLAN_KINDS = ["Advance Tax", "Self-Assessment Tax", "Regular Assessment Tax"];
const ITR_JSON_FORMS = ["ITR-1", "ITR-3", "ITR-4"];
const BLANK_CHALLAN = { kind: "Self-Assessment Tax", amount: 0, paid_date: "", bsr_code: "", challan_no: "", minor_head: "", notes: "" };

function Section({ title, subtitle, icon: Icon, right, children, testid }) {
  return (
    <div className="border border-gray-200 bg-white rounded" data-testid={testid}>
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          {Icon && <div className="w-8 h-8 rounded bg-gray-50 text-gray-600 flex items-center justify-center shrink-0"><Icon className="w-4 h-4" /></div>}
          <div className="min-w-0">
            <h3 className="font-semibold text-sm text-gray-900 truncate">{title}</h3>
            {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
          </div>
        </div>
        {right}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function Inner() {
  const { activeId } = useCompany();
  const { ay, isComputable } = useTaxYear();
  const navigate = useNavigate();

  const [suggestion, setSuggestion] = useState(null);
  const [computation, setComputation] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [reconcile, setReconcile] = useState(null);
  const [filing, setFiling] = useState({ status: "Not Filed", itr_form: "", filing_date: "", everify_date: "", ack_number: "", refund_amount: 0, demand_amount: 0, notes: "" });
  const [challanData, setChallanData] = useState({ rows: [], summary: {} });
  const [newChallan, setNewChallan] = useState(BLANK_CHALLAN);
  const [savingFiling, setSavingFiling] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = async () => {
    if (!activeId) return;
    try {
      const [s, f, ch, comps, al, rec] = await Promise.all([
        api.get(`/companies/${activeId}/tax/itr-suggestion?ay=${ay}`),
        api.get(`/companies/${activeId}/tax/filing-status?ay=${ay}`),
        api.get(`/companies/${activeId}/tax/challans?ay=${ay}`),
        api.get(`/companies/${activeId}/tax/computations?ay=${ay}`),
        api.get(`/companies/${activeId}/tax/alerts?ay=${ay}`),
        api.get(`/companies/${activeId}/tax/challans/reconcile?ay=${ay}`),
      ]);
      setSuggestion(s.data);
      if (f.data) setFiling({ status: f.data.status || "Not Filed", itr_form: f.data.itr_form || "", filing_date: f.data.filing_date || "", everify_date: f.data.everify_date || "", ack_number: f.data.ack_number || "", refund_amount: f.data.refund_amount || 0, demand_amount: f.data.demand_amount || 0, notes: f.data.notes || "" });
      else setFiling({ status: "Not Filed", itr_form: "", filing_date: "", everify_date: "", ack_number: "", refund_amount: 0, demand_amount: 0, notes: "" });
      setChallanData(ch.data || { rows: [], summary: {} });
      setComputation((comps.data || [])[0] || null);
      setAlerts(al.data?.alerts || []);
      setReconcile(rec.data || null);
    } catch (err) {
      toast.error(describeAxiosError(err));
    }
  };

  useEffect(() => { load(); }, [activeId, ay]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveFiling = async () => {
    setSavingFiling(true);
    try {
      await api.post(`/companies/${activeId}/tax/filing-status`, { ay, ...filing, refund_amount: Number(filing.refund_amount) || 0, demand_amount: Number(filing.demand_amount) || 0 });
      toast.success("Filing status saved");
      await load();
    } catch (err) { toast.error(describeAxiosError(err)); }
    finally { setSavingFiling(false); }
  };

  const useSuggestedForm = () => {
    if (suggestion?.form) setFiling((f) => ({ ...f, itr_form: suggestion.form }));
  };

  const addChallan = async () => {
    if (!(Number(newChallan.amount) > 0)) { toast.error("Enter a challan amount greater than 0"); return; }
    try {
      await api.post(`/companies/${activeId}/tax/challans`, { ay, ...newChallan, amount: Number(newChallan.amount) || 0 });
      toast.success("Challan added");
      setNewChallan(BLANK_CHALLAN);
      await load();
    } catch (err) { toast.error(describeAxiosError(err)); }
  };

  const deleteChallan = async (id) => {
    if (!window.confirm("Delete this challan?")) return;
    try {
      await api.delete(`/companies/${activeId}/tax/challans/${id}`);
      toast.success("Challan deleted");
      await load();
    } catch (err) { toast.error(describeAxiosError(err)); }
  };

  const pushToComputation = async () => {
    setPushing(true);
    try {
      const { data } = await api.post(`/companies/${activeId}/tax/imports/push-to-computation`, { ay, target: computation?.id || "new" });
      toast.success(data.created ? "New computation created from your imports & challans" : "Imports & challans applied to the existing computation");
      navigate(`/tax/computation/${data.id}`);
    } catch (err) { toast.error(describeAxiosError(err)); }
    finally { setPushing(false); }
  };

  const exportItrJson = async (form) => {
    if (!computation) { toast.error("Save a tax computation for this year first"); return; }
    setExporting(true);
    try {
      const { data } = await api.post(`/companies/${activeId}/tax/computations/${computation.id}/itr-json?form=${encodeURIComponent(form)}`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `AXIORA_${form.replace(/\s+/g, "")}_AY${ay}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${form} JSON downloaded`);
    } catch (err) { toast.error(describeAxiosError(err)); }
    finally { setExporting(false); }
  };

  const interest = (computation?.computed || {}).interest || {};
  const sm = challanData.summary || {};

  return (
    <>
      <PageHeader
        title={`Filing & Payments — AY ${ay}`}
        subtitle="ITR-form suggestion, filing-status tracker, and Challan-280 (advance / self-assessment tax) log."
        actions={
          <div className="flex items-center gap-2">
            <TaxYearSelect />
            <Button variant="outline" onClick={() => navigate("/tax")} className="h-8" data-testid="btn-filing-back">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          </div>
        }
      />
      <div className="p-6 space-y-6">
        {/* ── Filing / refund alerts ── */}
        {alerts.length > 0 && (
          <div className="space-y-2" data-testid="filing-alerts">
            {alerts.map((a, i) => (
              <div key={i} className={`flex items-start gap-2 border rounded p-3 ${a.level === "critical" ? "border-red-300 bg-red-50" : "border-amber-300 bg-amber-50"}`} data-testid={`alert-${a.level}`}>
                <Bell className={`w-4 h-4 mt-0.5 shrink-0 ${a.level === "critical" ? "text-red-600" : "text-amber-600"}`} />
                <div className="text-sm">
                  <span className={`font-semibold ${a.level === "critical" ? "text-red-800" : "text-amber-900"}`}>{a.title}</span>
                  <span className="text-gray-700"> — {a.detail}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── ITR-form suggestion ── */}
        <Section title="Recommended ITR form" subtitle={suggestion?.based_on_computation ? "Based on your latest saved computation for this year." : "Based on your taxpayer profile — save a computation for a sharper match."} icon={Sparkles} testid="section-itr-suggestion"
          right={computation ? (
            <div className="flex items-center gap-2" data-testid="itr-export-controls">
              {ITR_JSON_FORMS.includes(suggestion?.form) && (
                <Button onClick={() => exportItrJson(suggestion.form)} disabled={exporting} className="h-8 bg-blue-700 hover:bg-blue-800 text-xs" data-testid="btn-export-suggested-itr">
                  <Download className="w-3.5 h-3.5 mr-1" /> {exporting ? "Exporting…" : `Download ${suggestion.form} JSON`}
                </Button>
              )}
              <select onChange={(e) => { const v = e.target.value; e.target.value = ""; if (v) exportItrJson(v); }} defaultValue="" className="h-8 border border-gray-300 rounded px-2 text-xs bg-white" data-testid="select-export-itr">
                <option value="" disabled>Export other JSON…</option>
                {ITR_JSON_FORMS.map((f) => <option key={f} value={f}>{f} JSON</option>)}
              </select>
            </div>
          ) : <span className="text-xs text-gray-400">Save a computation to export ITR JSON</span>}
        >
          {suggestion ? (
            <div className="flex flex-col md:flex-row md:items-start gap-4">
              <div className="shrink-0">
                <div className="inline-flex items-center gap-2 px-3 py-2 rounded bg-blue-50 border border-blue-200" data-testid="itr-suggested-form">
                  <FileCheck2 className="w-5 h-5 text-blue-700" />
                  <span className="text-lg font-bold text-blue-800">{suggestion.form}</span>
                </div>
                <p className="text-xs text-gray-600 mt-1.5 max-w-[220px]">{suggestion.title}</p>
              </div>
              <div className="flex-1 space-y-2">
                {(suggestion.reasons || []).length > 0 && (
                  <ul className="text-xs text-gray-700 list-disc pl-4 space-y-0.5" data-testid="itr-reasons">
                    {suggestion.reasons.map((r, i) => <li key={i}>{r}</li>)}
                  </ul>
                )}
                {(suggestion.cautions || []).map((c, i) => (
                  <div key={i} className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5">{c}</div>
                ))}
              </div>
            </div>
          ) : <p className="text-xs text-gray-500">Loading…</p>}
        </Section>

        {/* ── Filing status tracker ── */}
        <Section title="Return filing status" subtitle="Track where this year's return stands, the acknowledgement, and any refund / demand." icon={Landmark} testid="section-filing-status"
          right={<Button onClick={saveFiling} disabled={savingFiling} className="h-8 bg-blue-700 hover:bg-blue-800 text-xs" data-testid="btn-save-filing">{savingFiling ? "Saving…" : "Save status"}</Button>}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-1">
              <Label className="text-[11px] uppercase tracking-wider text-gray-600">Status</Label>
              <select value={filing.status} onChange={(e) => setFiling({ ...filing, status: e.target.value })} className="h-9 w-full border border-gray-300 rounded px-2 text-sm bg-white" data-testid="select-filing-status">
                {FILING_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] uppercase tracking-wider text-gray-600 flex items-center justify-between">ITR form
                {suggestion?.form && <button type="button" onClick={useSuggestedForm} className="text-[10px] text-blue-700 hover:underline normal-case tracking-normal" data-testid="btn-use-suggested-form">Use {suggestion.form}</button>}
              </Label>
              <Input value={filing.itr_form} onChange={(e) => setFiling({ ...filing, itr_form: e.target.value })} placeholder="e.g. ITR-3" className="h-9" data-testid="input-itr-form" />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] uppercase tracking-wider text-gray-600">Acknowledgement no.</Label>
              <Input value={filing.ack_number} onChange={(e) => setFiling({ ...filing, ack_number: e.target.value })} placeholder="15-digit ITR-V no." className="h-9 font-mono" data-testid="input-ack-number" />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] uppercase tracking-wider text-gray-600">Filing date</Label>
              <Input type="date" value={filing.filing_date} onChange={(e) => setFiling({ ...filing, filing_date: e.target.value })} className="h-9" data-testid="input-filing-date" />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] uppercase tracking-wider text-gray-600">E-verify date</Label>
              <Input type="date" value={filing.everify_date} onChange={(e) => setFiling({ ...filing, everify_date: e.target.value })} className="h-9" data-testid="input-everify-date" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-[11px] uppercase tracking-wider text-emerald-700">Refund ₹</Label>
                <Input type="number" value={filing.refund_amount} onChange={(e) => setFiling({ ...filing, refund_amount: e.target.value })} className="h-9 text-right font-mono" data-testid="input-refund-amount" />
              </div>
              <div className="space-y-1">
                <Label className="text-[11px] uppercase tracking-wider text-red-700">Demand ₹</Label>
                <Input type="number" value={filing.demand_amount} onChange={(e) => setFiling({ ...filing, demand_amount: e.target.value })} className="h-9 text-right font-mono" data-testid="input-demand-amount" />
              </div>
            </div>
          </div>
        </Section>

        {/* ── Challan-280 tracker ── */}
        <Section title="Tax payments — Challan 280" subtitle="Log advance-tax & self-assessment-tax challans. These feed straight into the computation." icon={ReceiptIndianRupee} testid="section-challans"
          right={
            <Button onClick={pushToComputation} disabled={pushing || !isComputable} className="h-8 bg-emerald-700 hover:bg-emerald-800 text-xs disabled:opacity-50" data-testid="btn-push-to-computation">
              <ArrowRightCircle className="w-3.5 h-3.5 mr-1" /> {pushing ? "Applying…" : (computation ? "Apply to computation" : "Create computation")}
            </Button>
          }
        >
          {!isComputable && (
            <div className="mb-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2" data-testid="challan-noncomputable-note">
              Computing a return for AY {ay} is not supported yet — you can still log challans for your records.
            </div>
          )}
          {reconcile?.duplicate_count > 0 && (
            <div className="mb-3 border border-amber-300 bg-amber-50 rounded p-3" data-testid="challan-reconcile-warning">
              <div className="flex items-center gap-2 text-sm font-semibold text-amber-900">
                <AlertTriangle className="w-4 h-4" /> {reconcile.duplicate_count} possible double entr{reconcile.duplicate_count === 1 ? "y" : "ies"} detected
              </div>
              <ul className="text-xs text-amber-900 list-disc pl-5 mt-1 space-y-0.5">
                {reconcile.duplicates.map((d, i) => (
                  <li key={i}>{d.detail} <span className="text-amber-700">({d.source} · matched on {d.matched_on.join(", ")})</span></li>
                ))}
              </ul>
              <p className="text-[11px] text-amber-800 mt-1">A logged Challan-280 and a 26AS Part-C entry both feed the computation — delete the duplicate to avoid double-counting tax paid.</p>
            </div>
          )}
          {/* Summary chips */}
          <div className="flex flex-wrap gap-3 mb-4">
            {[["Advance Tax", sm.advance_tax], ["Self-Assessment Tax", sm.self_assessment_tax], ["Regular Assessment Tax", sm.regular_assessment_tax], ["Total paid", sm.total]].map(([k, v]) => (
              <div key={k} className="px-3 py-2 rounded border border-gray-200 bg-gray-50" data-testid={`challan-sum-${k.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="text-[10px] uppercase tracking-wider text-gray-500">{k}</div>
                <div className="text-sm font-mono font-semibold text-gray-900">₹ {formatINR(v || 0)}</div>
              </div>
            ))}
          </div>

          {/* 234B / 234C impact from latest computation */}
          {computation && (
            <div className="flex flex-wrap gap-4 mb-4 text-xs bg-blue-50/60 border border-blue-100 rounded px-3 py-2" data-testid="challan-234-impact">
              <span className="text-gray-600">Interest impact on latest computation:</span>
              <span>234B <b className="font-mono">₹ {formatINR(interest["234b"] || 0)}</b></span>
              <span>234C <b className="font-mono">₹ {formatINR(interest["234c"] || 0)}</b></span>
              <span>234A <b className="font-mono">₹ {formatINR(interest["234a"] || 0)}</b></span>
              <span className="text-gray-500">Add more advance-tax challans to reduce 234B / 234C.</span>
            </div>
          )}

          {/* Add challan form */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end mb-3 border border-dashed border-gray-300 rounded p-3 bg-gray-50/50">
            <div className="space-y-1 col-span-2 md:col-span-1">
              <Label className="text-[10px] uppercase tracking-wider text-gray-500">Kind</Label>
              <select value={newChallan.kind} onChange={(e) => setNewChallan({ ...newChallan, kind: e.target.value })} className="h-8 w-full border border-gray-300 rounded px-1 text-xs bg-white" data-testid="select-new-challan-kind">
                {CHALLAN_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider text-gray-500">Amount ₹</Label>
              <Input type="number" value={newChallan.amount} onChange={(e) => setNewChallan({ ...newChallan, amount: e.target.value })} className="h-8 text-right font-mono text-xs" data-testid="input-new-challan-amount" />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider text-gray-500">Paid date</Label>
              <Input type="date" value={newChallan.paid_date} onChange={(e) => setNewChallan({ ...newChallan, paid_date: e.target.value })} className="h-8 text-xs" data-testid="input-new-challan-date" />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider text-gray-500">BSR code</Label>
              <Input value={newChallan.bsr_code} onChange={(e) => setNewChallan({ ...newChallan, bsr_code: e.target.value })} className="h-8 text-xs font-mono" data-testid="input-new-challan-bsr" />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider text-gray-500">Challan no.</Label>
              <Input value={newChallan.challan_no} onChange={(e) => setNewChallan({ ...newChallan, challan_no: e.target.value })} className="h-8 text-xs font-mono" data-testid="input-new-challan-no" />
            </div>
            <Button onClick={addChallan} className="h-8 bg-blue-700 hover:bg-blue-800 text-xs" data-testid="btn-add-challan"><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>
          </div>

          {/* Challan table */}
          {(challanData.rows || []).length === 0 ? (
            <div className="text-xs text-gray-500 py-4 text-center bg-gray-50 rounded" data-testid="challans-empty">No challans logged for AY {ay} yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="tx-table w-full text-xs" data-testid="challans-table">
                <thead>
                  <tr><th>Kind</th><th>Paid date</th><th>BSR</th><th>Challan no.</th><th className="text-right">Amount</th><th></th></tr>
                </thead>
                <tbody>
                  {challanData.rows.map((r) => (
                    <tr key={r.id} data-testid={`challan-row-${r.id}`}>
                      <td>{r.kind}</td>
                      <td className="font-mono">{r.paid_date || "—"}</td>
                      <td className="font-mono">{r.bsr_code || "—"}</td>
                      <td className="font-mono">{r.challan_no || "—"}</td>
                      <td className="text-right font-mono font-semibold">₹ {formatINR(r.amount || 0)}</td>
                      <td><button onClick={() => deleteChallan(r.id)} className="text-red-600 hover:text-red-800" data-testid={`btn-delete-challan-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>
      </div>
    </>
  );
}

export default function FilingAndPayments() {
  return <RequireCompany><Inner /></RequireCompany>;
}
